import { PDeleteConfirmation } from "@/components/PDeleteConfirmation/PDeleteConfirmation";
import { DeleteImpactSummary, impactHasLines } from "@/components/PBulkActionConfirmation/DeleteImpactSummary";
import { trashOf } from "@/lib/guests/trashCopy";
import { IconArrowBackUp, IconCheck, IconTrash, IconX } from "@tabler/icons-react";
import React from "react";
import { useCompanionEntry } from "../hooks/useCompanionEntry";
import { CoveringLoadingSpinner } from "@/components/LoadingSpinner/CoveringLoadingSpinner";
import { AcceptanceStateChip } from "@/components/AcceptanceStateChip/AcceptanceStateChip";
import { useTranslations } from "next-intl";
import { Guest } from "@/types/Guest";
import { AnimatePresence } from "framer-motion";
import { useRole } from "@/providers/RoleProvider";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { useGuestCompanions } from "@/providers/GuestCompanions";
import { usePreviewWriteGuard } from "@/lib/preview/usePreviewWriteGuard";
import { describeBatchUpdateError } from "@/lib/i18n/translateBackendError";

type CompanionsEntryProps = {
  companion: Guest;
  /** The host whose panel this row is rendered in — the fallback when the row
   *  itself carries no `mainGuest` (a narrow projection), so a delete can still
   *  tell the provider whose count moved. */
  hostGuestId?: string;
  setOpeningId: React.Dispatch<React.SetStateAction<string | Guest>>;
};

export const CompanionsEntry: React.FC<CompanionsEntryProps> = ({
  companion: companionProps,
  hostGuestId,
  setOpeningId,
}) => {
  const t = useTranslations("companion");
  const tGuest = useTranslations("guestDetails");
  const tErrors = useTranslations("errors");
  /**
   * The whole error, so a per-row reason of the guest batch speaks
   * (`contingentExceeded` instead of "Some guests could not be saved.") and a
   * code without copy reads as the unknown line, never as itself (M-01d).
   */
  const describeActionError = (error: unknown) =>
    error === null || error === undefined
      ? undefined
      : describeBatchUpdateError(tErrors, error, tErrors("unknown"));
  const { legacyRoleFloor } = useRole();
  const { can } = useCapabilities();
  const tBulk = useTranslations("bulkConfirm");
  const { guard } = usePreviewWriteGuard();
  const { eventId } = useGuestCompanions();
  const {
    companion,
    rowKind,
    isDeleting,
    localLoading,
    actionError,
    deleteImpact,
    handleOpenDeleteConfirmation,
    handleOpenHardDeleteConfirmation,
    handleCloseDeleteConfirmation,
    handleApproveCompanion,
    handleDeclineCompanion,
    handleDeleteCompanion,
    handleRestoreCompanion,
    formatCompanionName,
  } = useCompanionEntry(companionProps, hostGuestId);

  /**
   * A removal is a DELETE, and only a caller who may delete gets the button.
   *
   * Deliberately no fallback to "decline" for a role without `guests:delete`:
   * that silent substitution is the entire bug this replaces — the dialog said
   * "löschen … kann nicht rückgängig gemacht werden" and the write left the row
   * in place. A role that cannot delete sees no removal affordance here and can
   * still archive the companion from its own detail popup.
   */
  const mayDelete = can("guests:delete");
  /**
   * Restore writes (`unarchiveGuests` / the accept lane both go through
   * `update-batch`), so it needs the write capability — it was the only
   * writing control in this row behind nothing but the VIEWER floor, which for
   * a CUSTOM role can be reached with any of ~20 unrelated write caps. The
   * backend refuses such a caller; the button just promised otherwise.
   */
  const mayWrite = can("guests:write");
  const isRemoval = rowKind !== "request";
  // W6: the removal (the companion's real delete) moves the row to the trash
  // when the impact says the backend will; the reject dialog is a decline and
  // never changes. No `trash` in the impact ⇒ exactly today's sentence. The
  // dialog offers no way around the trash (22.10., owner).
  const trashMode = isRemoval && trashOf(deleteImpact) !== null;
  // Audit-B M1: before the impact lands the removal sentence promises neither
  // "permanent" nor "trash" and the confirm waits; a failed impact keeps a
  // neutral sentence. The reject dialog fetches no impact and never waits.
  const impactPending = isRemoval && deleteImpact === undefined;
  const impactFailed = isRemoval && deleteImpact === null;

  const openConfirmation = () => {
    if (isRemoval) {
      handleOpenHardDeleteConfirmation(eventId);
      return;
    }
    handleOpenDeleteConfirmation();
  };

  return (
    <div className="flex flex-col border-b outline-quaternary py-3">
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row items-center gap-2">
          <button
            className="body-2-medium hover:underline"
            onClick={() => setOpeningId(companion)}
          >
            {formatCompanionName(t("noName"))}
          </button>
          {/* Only rows that are NOT part of the party carry a badge. Labelling
              the ordinary ones too would turn the common case into noise — and
              the panel's count means the party, so a row that is not in it must
              say so or the two numbers look wrong. */}
          {rowKind !== "party" && companion.archived !== true && (
            <AcceptanceStateChip
              state={companion.acceptanceState as string | undefined}
              testId={`companion-state-${companion.guestId ?? ""}`}
            />
          )}
          {companion.archived === true && (
            <span
              className="inline-flex items-center rounded-full border px-2 py-0.5 body-4-regular w-max user-icon-font-1 user-icon-bg-1 outline-user-icon-1"
              data-testid={`companion-archived-${companion.guestId ?? ""}`}
            >
              {t("archivedCompanion")}
            </span>
          )}
        </div>
        <div className="flex-row">
          {legacyRoleFloor === "VIEWER" ? null : rowKind === "request" ? (
            <div className="flex flex-row items-center gap-2">
              <button
                onClick={openConfirmation}
                title={t("rejectCompanion")}
                data-testid={`companion-reject-${companion.guestId ?? ""}`}
              >
                <IconX className="font-error" size={16} />
              </button>
              <button
                onClick={handleApproveCompanion}
                data-testid={`companion-approve-${companion.guestId ?? ""}`}
              >
                <IconCheck className="font-success" size={16} />
              </button>
            </div>
          ) : (
            <div className="flex flex-row items-center gap-2">
              {rowKind === "inactive" && mayWrite && (
                <button
                  onClick={guard(() => void handleRestoreCompanion())}
                  title={t("restoreCompanion")}
                  data-testid={`companion-restore-${companion.guestId ?? ""}`}
                >
                  <IconArrowBackUp className="font-success" size={16} />
                </button>
              )}
              {mayDelete && (
                <button
                  onClick={guard(openConfirmation)}
                  title={tGuest("delete")}
                  data-testid={`companion-delete-${companion.guestId ?? ""}`}
                >
                  <IconTrash className="font-error" size={16} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {/* The row's own error surface, for the actions that have no dialog to
          report into (approve, restore). The provider-wide banner is NOT an
          option: `CompanionsList` renders it instead of the rows, so it would
          unmount the row that is trying to explain itself. */}
      {actionError != null && !isDeleting && (
        <p
          className="body-4-regular font-error pt-1"
          data-testid={`companion-row-error-${companion.guestId ?? ""}`}
        >
          {describeActionError(actionError)}
        </p>
      )}
      <AnimatePresence>
        {isDeleting && (
          <PDeleteConfirmation
            title={
              isRemoval
                ? trashMode
                  ? t("trashConfirmationCompanion")
                  : t("deleteConfirmationCompanion")
                : t("rejectConfirmationCompanion")
            }
            message={
              isRemoval
                ? impactPending
                  ? tBulk("impactPending")
                  : impactFailed
                    ? tBulk("impactUnavailableCompanion")
                    : trashMode
                      ? // The companion's own plain question — the retention is
                        // not spelled out (owner, 22.10. and 24.10.).
                        t("trashQuestionCompanion")
                      : t("deleteConfirmationCompanionMessage")
                : t("rejectConfirmationCompanionMessage")
            }
            liveMessage={isRemoval}
            confirmDisabled={impactPending}
            messageTestId={trashMode ? "guest-trash-confirm-copy" : undefined}
            primaryButtonText={trashMode ? tGuest("moveToTrash") : undefined}
            onCloseAction={handleCloseDeleteConfirmation}
            onConfirmAction={
              isRemoval
                ? (e, typed) => handleDeleteCompanion(e, typed, { eventId })
                : handleDeclineCompanion
            }
            isLoading={localLoading}
            error={describeActionError(actionError)}
          >
            {/* What the delete takes with it — seats, sessions, sent mails.
                Shown here rather than one popup deeper, because this is where
                the decision is made. */}
            {isRemoval && deleteImpact && (!trashMode || impactHasLines(deleteImpact)) ? (
              <DeleteImpactSummary impact={deleteImpact} trash={trashMode} />
            ) : null}
          </PDeleteConfirmation>
        )}
      </AnimatePresence>
      {localLoading && <CoveringLoadingSpinner />}
    </div>
  );
};
